import { useState } from "react";
import { Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSalesforceIntegrationDetailsContext } from "../../contexts/useSalesforceIntegrationDetailsContext";
import { useUpdateIntegrationSyncSettings } from "../../hooks/useUpdateIntegrationSyncSettings";
import { useDeleteIntegration } from "../../hooks/useDeleteIntegration";
import { useSettingsValidation } from "../../hooks/useSettingsValidation";
import { useSnackbar } from "../../../../components/Snackbar";
import { DeleteIntegrationConfirmDialog } from "./DeleteIntegrationConfirmDialog";
import { ValidationErrorDialog } from "./ValidationErrorDialog";
import { ACTION_MESSAGES } from "./constants";
import {
  SFDCIntegrationSyncSettings,
  IntegrationSyncSettingsInput,
  EnrichmentSynchronizationState,
} from "../../types";
import { useSalesforcePageContext } from "../../contexts/useSalesforcePageContext";
import { sleep } from "../../../../utils/misc";

// Utility function to map SFDCIntegrationSyncSettings to IntegrationSyncSettingsInput
const mapToSyncSettingsInput = (
  settings: SFDCIntegrationSyncSettings
): IntegrationSyncSettingsInput => ({
  autoSync: settings.autoSync,
  syncAccounts: settings.syncAccounts,
  syncLeads: settings.syncLeads,
  exportTechInstalls: settings.exportTechInstalls,
  exportItSpend: settings.exportItSpend,
  exportFirmographics: settings.exportFirmographics,
  exportAccountScoring: settings.exportAccountScoring,
  exportCloudDynamics: settings.exportCloudDynamics,
  useCountryLevelMatching: settings.useCountryLevelMatching,
  accountsCountryField: settings.accountsCountryField,
  localizeInstalls: settings.localizeInstalls,
  customHgIdMatching: settings.customHgIdMatching,
});

export const SalesforceSettingsActions = () => {
  const { refetch: refetchIntegrations, setShowBackdrop } =
    useSalesforcePageContext();
  const {
    integration,
    synchronizationState,
    setIsSettingsModalOpen,
    refetchSynchronization,
    draftSettings,
  } = useSalesforceIntegrationDetailsContext();
  const { showError, showInfo } = useSnackbar();
  const navigate = useNavigate();

  // State for delete confirmation dialog
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // State for validation error dialog
  const [showValidationError, setShowValidationError] = useState(false);

  // Compute isInitialSetup locally
  const isInitialSetup =
    synchronizationState === EnrichmentSynchronizationState.NEVER_RAN_MATCHING;

  // Delete integration hook
  const { deleteIntegration } = useDeleteIntegration({
    onCompleted: async () => {
      if (!isInitialSetup) {
        showInfo(ACTION_MESSAGES.deleteSuccess);
      } else {
        showInfo(ACTION_MESSAGES.integrationCancelled);
      }

      await refetchIntegrations();
      setIsSettingsModalOpen(false);
      setShowBackdrop(false);
      navigate("/integrations/salesforce");
    },
    onError: (error) => {
      setShowBackdrop(false);
      showError(`${ACTION_MESSAGES.deleteError} ${error.message}`);
    },
  });

  const { updateSyncSettings, loading } = useUpdateIntegrationSyncSettings({
    onCompleted: async () => {
      if (isInitialSetup) {
        // Wait for 1 second to ensure the sync in the backend is started
        await sleep(1000);
        await refetchIntegrations();
        await refetchSynchronization();
        showInfo(ACTION_MESSAGES.firstSaveSettingsSuccess);
      } else {
        showInfo(ACTION_MESSAGES.saveSettingsSuccess);
      }
      // Close the dialog and hide backdrop
      setIsSettingsModalOpen(false);
      setShowBackdrop(false);
    },
    onError: (error) => {
      setShowBackdrop(false);
      showError(ACTION_MESSAGES.saveSettingsError);
      console.error("Failed to update sync settings:", error);
    },
  });

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    if (integration?.id) {
      setShowBackdrop(true);
      await deleteIntegration({ id: integration.id });
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // Use centralized validation hook with draft settings
  const { isValid: settingsValid, errors: validationErrors } =
    useSettingsValidation(integration, draftSettings);

  const handleCloseValidationError = () => {
    setShowValidationError(false);
  };

  const handleSaveAndContinue = async () => {
    if (!integration || !draftSettings) return;

    // Validate settings before saving using centralized validation
    if (!settingsValid) {
      setShowValidationError(true);
      return;
    }

    try {
      setShowBackdrop(true);

      await updateSyncSettings({
        integrationId: integration.id,
        syncSettings: mapToSyncSettingsInput(draftSettings),
      });
    } catch (error) {
      setShowBackdrop(false);
      showError(ACTION_MESSAGES.saveSettingsError);
      console.error("Error saving settings:", error);
    }
  };

  const handleCancel = () => {
    if (isInitialSetup) {
      // In initial setup, cancel means delete the integration
      handleDelete();
    } else {
      // In normal mode, cancel means close the dialog
      setIsSettingsModalOpen(false);
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" flex={1}>
        {/* Left side button - Delete or Cancel depending on mode */}
        <Button
          data-testid="salesforce-settings-delete-btn"
          id={`hgForce-autosync-${isInitialSetup ? "cancel" : "delete"}`}
          onClick={isInitialSetup ? handleConfirmDelete : handleDelete}
          variant="text"
          color="error"
          disabled={loading}
          size="small"
        >
          {isInitialSetup ? "Cancel" : "Delete"}
        </Button>

        {/* Right side buttons */}
        <Stack direction="row" spacing={1}>
          {!isInitialSetup && (
            <Button
              data-testid="salesforce-settings-cancel-btn"
              id="hgForce-autosync-cancel-right"
              onClick={handleCancel}
              variant="text"
              color="secondary"
              size="small"
            >
              Cancel
            </Button>
          )}

          <Button
            data-testid="salesforce-settings-save-btn"
            id="hgForce-autosync-save"
            onClick={handleSaveAndContinue}
            variant="contained"
            color="primary"
            size="small"
            disabled={loading}
          >
            Save
          </Button>
        </Stack>
      </Stack>

      {/* Delete Confirmation Dialog */}
      <DeleteIntegrationConfirmDialog
        open={showDeleteConfirm}
        integration={integration}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      {/* Validation Error Dialog */}
      <ValidationErrorDialog
        open={showValidationError}
        onClose={handleCloseValidationError}
        errors={validationErrors}
      />
    </>
  );
};
